import * as React from "react"
import {Check} from "lucide-react"
import {Dialog, DialogContent, DialogTitle} from "@/components/ui/dialog"
import {cn} from "@/lib/utils"
import {navigateToUrl} from "@/main/common/BrowserNavigation"
import {MetaHandler} from "@/main/common/MetaHandler"
import {LocalizationUtil} from "@/main/util/LocalizationUtil"
import {resolveContextualAccountAccessDialogOptions} from "@/main/react/account_access/AccountAccessDialogContext"
import {openAccountAccessDialog} from "@/main/react/account_access/AccountAccessDisplayerDialog"
import {useBackDismissesOverlay} from "@/main/navigation/useBackDismissesOverlay"
import type {Localizer} from "@/main/react/Types"
import type {DropdownItem, DropdownSection} from "./CtaButtonDropdown"

/**
 * The COARSE-POINTER presentation of a `CtaButtonDropdown` overflow menu: the same sections as a bottom
 * sheet with a Cancel row. `PROFILE-80` (board settle round 41 V35 a, 2026-09-30).
 *
 * <p><b>Why.</b> On a phone the `…` menu opened as an anchored popover mid-screen, over someone else's post,
 * with no backdrop and no dismiss row, at the moment a member may be deciding to block. A sheet puts every
 * row in the thumb zone, gives an obvious way out, and frames the safety group as a deliberate choice.
 *
 * <p><b>Mounted only by an opted-in dropdown</b> (`coarsePointerSheet`), and only rendered open after a TAP
 * on a coarse-pointer device. Every other dropdown never mounts this component, so it registers no history
 * guard and renders nothing. The popover is untouched for a mouse, a keyboard and a fine pointer.
 *
 * <p>Rules this obeys, the same list the chat action sheet (the primitive's precedent, mobile chat work
 * order D16) obeys:
 * <ul>
 *   <li><b>Rule 33.</b> Back closes the sheet and leaves the member where they were, through
 *       `useBackDismissesOverlay`, never a hand-written history entry.</li>
 *   <li><b>Rule 23.</b> `DialogContent mobilePresentation="sheet"`: a bottom sheet below `sm`, centred above.
 *       Rows carry the dropdown's own `dropdown-item-<id>` hooks, so a spec driving either presentation finds
 *       the same verbs, and the sections keep their divider (general, then moderation).</li>
 *   <li>An item's action runs AFTER the sheet has closed (`onCloseAutoFocus`), so a verb that opens its own
 *       dialog (Report, Block, sign-in) is not fighting the closing sheet for focus.</li>
 * </ul>
 */

const BACK_GUARD_MARKER = "seavienCtaDropdownSheet"

const ROW_CLASS = cn(
    "flex min-h-11 w-full items-center gap-3 rounded-lg px-3 text-left text-[15px] text-foreground",
    "standard-hover focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50",
    "disabled:pointer-events-none disabled:opacity-50",
    "[&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:text-muted-foreground",
)

const DESTRUCTIVE_ROW_CLASS = "text-destructive-text [&_svg]:text-destructive-text"

export type CtaDropdownSheetProps = {
    open: boolean
    onClose: () => void
    sections: DropdownSection[]
    /** The sheet's accessible name: the trigger's own name (`ariaLabel`, else its visible text). */
    title: string
    localizer: Localizer
}

export function CtaDropdownSheet({open, onClose, sections, title, localizer}: CtaDropdownSheetProps) {
    const pendingActionRef = React.useRef<(() => void) | null>(null)

    useBackDismissesOverlay({open, onRequestClose: onClose, marker: BACK_GUARD_MARKER})

    const runAfterClose = (action: () => void) => {
        pendingActionRef.current = action
        onClose()
    }

    const handleCloseAutoFocus = (event: Event) => {
        const action = pendingActionRef.current
        pendingActionRef.current = null
        if (!action) {
            return
        }
        event.preventDefault()
        action()
    }

    // The same decision the popover's `onSelect` makes, in the same order: a login-gated verb opens the
    // account-access dialog for a signed-out member; otherwise the item's own handler, else its href.
    const activate = (item: DropdownItem) => {
        if (item.requiresLogin && !MetaHandler.isUserLoggedIn()) {
            // `LocalizationUtil.get`, exactly as the popover passes it: the account-access dialog is mounted
            // at root and takes the root localizer, so both presentations open the identical dialog.
            openAccountAccessDialog(LocalizationUtil.get, resolveContextualAccountAccessDialogOptions(LocalizationUtil.get, item.accountAccessDialog))
            return
        }
        if (item.onClick) {
            void item.onClick()
        } else if (item.href) {
            navigateToUrl(item.href, {
                source: item.id ? `cta-dropdown:${item.id}` : "cta-dropdown",
            })
        }
    }

    return (
        <Dialog
            open={open}
            onOpenChange={(nextOpen) => {
                if (!nextOpen) {
                    onClose()
                }
            }}
        >
            <DialogContent
                mobilePresentation="sheet"
                showCloseButton={false}
                className="gap-1 sm:max-w-sm md:max-w-sm"
                aria-describedby={undefined}
                onCloseAutoFocus={handleCloseAutoFocus}
                data-playwright-id="cta-dropdown-sheet"
            >
                <DialogTitle className="sr-only">{title}</DialogTitle>
                <div className="flex flex-col gap-0.5">
                    {sections.map((section, sectionIndex) => (
                        <React.Fragment key={section.id ?? `section-${sectionIndex}`}>
                            {sectionIndex > 0 ? (
                                <div role="separator" className="my-1 h-px bg-border" data-playwright-id="cta-dropdown-sheet-separator"/>
                            ) : null}
                            {section.title ? (
                                <div className="px-3 py-1.5 text-xs text-muted-foreground">{section.title}</div>
                            ) : null}
                            {section.items.map((item, itemIndex) => {
                                const Icon = item.icon
                                return (
                                    <button
                                        key={item.id ?? `${item.text ?? "item"}-${itemIndex}`}
                                        type="button"
                                        className={cn(ROW_CLASS, item.destructive && DESTRUCTIVE_ROW_CLASS, item.extraClass)}
                                        data-playwright-id={item.id ? `dropdown-item-${item.id}` : undefined}
                                        data-variant={item.destructive ? "destructive" : "default"}
                                        disabled={item.disabled}
                                        aria-pressed={item.checked}
                                        onClick={() => runAfterClose(() => activate(item))}
                                    >
                                        {Icon ? <Icon aria-hidden="true"/> : null}
                                        {item.text != null ? <span>{item.text}</span> : null}
                                        {item.checked ? (
                                            <Check aria-hidden="true" data-playwright-id="dropdown-item-checked" className="ml-auto"/>
                                        ) : null}
                                    </button>
                                )
                            })}
                        </React.Fragment>
                    ))}
                    {/* The sheet's own way out, the row `PROFILE-80` asks for. Last, and separated from the
                        verbs, so a thumb reaching for "cancel" never lands on Block. */}
                    <div role="separator" className="my-1 h-px bg-border"/>
                    <button
                        type="button"
                        className={cn(ROW_CLASS, "justify-center font-medium")}
                        data-playwright-id="cta-dropdown-sheet-cancel"
                        onClick={onClose}
                    >
                        {localizer("literal.cancel")}
                    </button>
                </div>
            </DialogContent>
        </Dialog>
    )
}
